import { ChannelUtil } from '@commons/util/helper/channel';
import { Store } from '@ngrx/store';
import { copy } from 'angular';
import { forkJoin, Observable, switchMap } from 'rxjs';

import {
  Acceptation,
  Activable,
  AgentConfigFacade,
  AgentConfigModel,
  AgentSkillFacade,
  AgentUserConfService,
  Channel,
  ChannelType,
  Group,
  InterContact,
  isAgentReady,
  MediaSkill,
  MixableChannel,
  OpeningChannel,
  Option,
  OutgoingCallDisplayedNumber,
  Skill,
  Team,
  User,
  waitToLoad,
} from '@contact/upgrade';

const MODULE_NAME = 'agentConfigDeprecatedService';

/**
 * @deprecated
 */
export interface IAgentConfigDeprecatedService {
  /**
   * @deprecated prefer AgentConfigFacade
   */
  domainGroups: Group[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  domainTeams: Team[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  domainSkills: Skill[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  channels: Channel[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  channelTypes: ChannelType[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  agentGroups: Group[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  agentTeam: Option<Team>;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  agentSupervisors: User[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  outgoingCallDisplayedNumbers: OutgoingCallDisplayedNumber[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  outgoingCallSkills: Skill[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  outgoingCallPreferredSkillUuid: string;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  maxMailBasketByPage: number;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  metricChannels: { [channel: string]: string[] };
  /**
   * @deprecated prefer AgentConfigFacade
   */
  visibleSkillIds: string[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  interContactConfig: { [channel: string]: Option<InterContact> };
  /**
   * @deprecated prefer AgentConfigFacade
   */
  acceptationConfig: { [channel: string]: Acceptation | null };
  /**
   * @deprecated prefer AgentConfigFacade
   */
  mixableChannels: MixableChannel[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  openingChannels: OpeningChannel[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  customerRepositoryName: string;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  getUnactivableSkillIds(): string[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  getChatIadvizeServiceUrl(): Option<string>;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  hasChannel(type: ChannelType): boolean;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  hasChannels(...channelTypes: ChannelType[]): boolean;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  channelHasSkills(type: ChannelType): boolean;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  hasPhoneChannel(): boolean;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  initialView(): string;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  canRefuseContact(channel: Channel): boolean;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  channelSkills(channel: string): MediaSkill[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  channelActiveSkills(channel: string): MediaSkill[];
  /**
   * @deprecated prefer AgentConfigFacade
   */
  isSkillEnabledForChannel(channel: Channel, skill: Skill): boolean;
  /**
   * @deprecated prefer AgentConfigFacade
   */
  getChatSlaConfiguration(): any;
  /**
   * @deprecated
   */
  getDomainSkillsForChannel(channel: Channel): Observable<Skill[]>;
  /**
   * @deprecated
   */
  getSkillsInheritedOfUserDomainForChannel(channel: Channel): Observable<Skill[]>;
}

export class AgentConfigDeprecatedService implements IAgentConfigDeprecatedService {
  static $inject: Array<string> = ['ngrxStoreService', 'agentConfigService', 'agentConfigFacade', 'agentSkillFacade'];

  public name = MODULE_NAME;

  private _agentConfig: AgentConfigModel;
  private _skills: Activable<MediaSkill>[] = [];
  private _domainGroups: Group[] = [];
  private _domainTeams: Team[] = [];
  private _domainSkills: Skill[] = [];

  constructor(
    private readonly store$: Store,
    private readonly agentUserConfService: AgentUserConfService,
    private readonly agentConfigFacade: AgentConfigFacade,
    private readonly agentSkillFacade: AgentSkillFacade
  ) {
    this.agentConfigFacade.config$.subscribe(config => (this._agentConfig = config));
    this.agentSkillFacade.activable$.subscribe(skills => (this._skills = skills));
    this.store$
      .pipe(
        waitToLoad([isAgentReady]),
        switchMap(() =>
          forkJoin(
            this.agentUserConfService.getDomainGroups(),
            this.agentUserConfService.getDomainTeams(),
            this.agentUserConfService.getDomainSkills()
          )
        )
      )
      .subscribe(([groups, teams, skills]) => {
        groups.sort((a, b) => this.compareIgnoreCase(a.name, b.name));
        teams.sort((a, b) => this.compareIgnoreCase(a.name, b.name));
        this._domainGroups = groups;
        this._domainTeams = teams;
        this._domainSkills = skills;
      });
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get domainGroups(): Group[] {
    return this._domainGroups;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get domainTeams(): Team[] {
    return this._domainTeams;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get domainSkills(): Skill[] {
    return this._domainSkills;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get channels(): Channel[] {
    return this._agentConfig.channels;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get channelTypes(): ChannelType[] {
    return this._agentConfig.channels.reduce<ChannelType[]>((result, channel) => {
      let { type } = channel;
      if (result.indexOf(type) !== -1) {
        return result;
      }
      return [...result, type];
    }, []);
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  hasChannel(type: ChannelType): boolean {
    return this.channels.filter(channel => channel.type === type).length > 0;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  hasChannels(...channelTypes: ChannelType[]): boolean {
    return channelTypes.every(channelType => this.channelTypes.indexOf(channelType) >= 0);
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  hasPhoneChannel(): boolean {
    return this.hasChannel(ChannelType.PHONE);
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get customerRepositoryName(): string {
    return this._agentConfig.customerRepositoryName || '';
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get agentGroups(): Group[] {
    return this._agentConfig.groups;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get agentTeam(): Option<Team> {
    return Option.of(this._agentConfig.team);
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get agentSupervisors(): User[] {
    return this._agentConfig.supervisors;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get outgoingCallDisplayedNumbers(): OutgoingCallDisplayedNumber[] {
    return this._agentConfig.outgoingCallDisplayedNumbers;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get outgoingCallSkills(): Skill[] {
    return this._agentConfig.outgoingCallSkills;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get outgoingCallPreferredSkillUuid(): string {
    return this._agentConfig.outgoingCallPreferredSkillUuid || '';
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get interContactConfig(): { [channel: string]: Option<InterContact> } {
    return [...this._agentConfig.ids].reduce<{ [channel: string]: Option<InterContact> }>(
      (result, id) => ({
        ...result,
        [id]: Option.of(this._agentConfig.entities[id]).flatMap(config => Option.of(config.interContact)),
      }),
      {}
    );
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get acceptationConfig(): { [channel: string]: Acceptation | null } {
    return [...this._agentConfig.ids].reduce<{ [channel: string]: Acceptation | null }>(
      (result, id) => ({
        ...result,
        [id]: Option.of(this._agentConfig.entities[id])
          .flatMap(config => Option.of(config.acceptation))
          .getOrNull(),
      }),
      {}
    );
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get maxMailBasketByPage(): number {
    return this._agentConfig.mail.maxMailBasketByPage;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get mixableChannels(): MixableChannel[] {
    return this._agentConfig.mixableChannels;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get openingChannels(): OpeningChannel[] {
    return this._agentConfig.openingChannels;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get visibleSkillIds(): string[] {
    return this._agentConfig.visibleSkillIds;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  getUnactivableSkillIds(): string[] {
    return this._agentConfig.unactivableSkillIds;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  get metricChannels(): { [channel: string]: string[] } {
    return Object.keys(this._agentConfig.entities).reduce<{ [channel: string]: string[] }>(
      (result, id) => ({
        ...result,
        [id]: Option.of(this._agentConfig.entities[id])
          .map(config => config.metrics)
          .getOrElse([]),
      }),
      {}
    );
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  autoAcceptContact(): boolean {
    return this._agentConfig.automaticAcceptCall;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  canRefuseContact(channel: Channel): boolean {
    let channelConf = this._agentConfig.entities[ChannelUtil.toString(channel)];
    return channelConf && channelConf.acceptation ? channelConf.acceptation.refuseCall : false;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  initialView(): string {
    return this._agentConfig.initialView || '';
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  getChatSlaConfiguration(): any {
    return this._agentConfig.chat.slaBySkills;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  getChatIadvizeServiceUrl(): Option<string> {
    return Option.of(this._agentConfig.iAdvizeChat.chatIadvizeServiceUrl);
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  channelHasSkills(type: ChannelType): boolean {
    return this._skills.filter(({ entity: skill }) => skill.channel.type === type).length > 0;
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  channelSkills(channel: string): MediaSkill[] {
    return this._skills
      .map(({ entity }) => entity)
      .filter(skill => ChannelUtil.toString(skill.channel) === channel)
      .map(mediaSKill => copy(mediaSKill));
  }

  /**
   * @deprecated prefer AgentConfigFacade
   */
  channelActiveSkills(channel: string): MediaSkill[] {
    return this._skills
      .filter(({ active }) => active)
      .map(({ entity }) => entity)
      .filter(skill => ChannelUtil.toString(skill.channel) === channel)
      .map(mediaSKill => copy(mediaSKill));
  }

  /**
   * Used to know if a channel has an active skill  or not.
   * return false if the channel doesn't have the skill
   */
  isSkillEnabledForChannel(channel: Channel, s: Skill): boolean {
    return (
      this._skills
        .filter(({ entity }) => ChannelUtil.areSame(entity.channel, channel))
        .filter(({ entity }) => entity.skill.id === s.id)
        .filter(({ active }) => active).length > 0
    );
  }

  /**
   * @deprecated
   */
  getDomainSkillsForChannel(channel: Channel): Observable<Skill[]> {
    return this.agentUserConfService.getDomainSkillsForChannel(channel);
  }

  /**
   * @deprecated
   */
  getSkillsInheritedOfUserDomainForChannel(channel: Channel): Observable<Skill[]> {
    return this.agentUserConfService.getSkillsInheritedOfUserDomainForChannel(channel);
  }

  /**
   * one more identically function to sort
   */
  private compareIgnoreCase(a: string, b: string): number {
    if (a == null && b == null) {
      return 0;
    }
    if (a == null) {
      return -1;
    }
    if (b == null) {
      return 1;
    }
    const la: string = a.toLowerCase();
    const lb: string = b.toLowerCase();
    return la === lb ? 0 : la < lb ? -1 : 1;
  }
}

export default MODULE_NAME;
